<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="fixed z-10 inset-0 overflow-y-auto" >
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
          <div class="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full sm:p-6">

            <!-- Pasek statusu -->
            <div class="mb-3 flex items-center justify-between">
              <div class="text-sm text-gray-600">
                <template v-if="isLoading">Ładowanie okresów… ({{periods.length}} znalezionych)</template>
                <template v-else>Załadowane okresy: {{periods.length}}</template>
              </div>
              <div class="flex gap-2">
                <button type="button" @click="loadFirstPage()" :disabled="isLoading"
                        class="px-3 py-1.5 rounded-md bg-slate-100 text-sm hover:bg-slate-200 disabled:opacity-50">
                  Odśwież
                </button>
                <button type="button" @click="loadMore()" :disabled="!canLoadMore || isLoading"
                        class="px-3 py-1.5 rounded-md bg-slate-100 text-sm hover:bg-slate-200 disabled:opacity-50">
                  Załaduj więcej
                </button>
              </div>
            </div>

            <div class="overflow-auto h-96">
                <table class="min-w-full divide-y divide-gray-300">
                    <thead>
                    <tr>
                        <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3">Okres</th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Ilość faktur</th>
                        <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-3">
                        <span class="sr-only">Edit</span>
                        </th>
                    </tr>
                    </thead>
                    <tbody class="bg-white">
                    <tr v-for="(row, idx) in periods" :key="row.key" :class="idx % 2 === 0 ? undefined : 'bg-gray-50'">
                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                          <div class="flex flex-col">
                            <span>{{ row.key }}</span>
                            <span v-if="row.parsed" class="text-xs text-gray-500">{{ row.parsed.startISO }} → {{ row.parsed.endISO }}</span>
                          </div>
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <span v-if="row.meta.loading">…</span>
                          <span v-else-if="row.meta.count !== null">{{ row.meta.count }}</span>
                          <span v-else>—</span>
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <button @click.prevent="generateCSV(row.key)" v-if="row.hasPayerNIP" type="button" class="rounded bg-blue-500 px-2 py-1 text-sm font-semibold text-white shadow-sm hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500">Generuj CSV</button>
                            <span class="text-gray-200" v-else>Brak danych</span>
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <button @click.prevent="generateEDI(row.key)" :disabled="checkPeriod(row.key)" v-if="row.hasPayerNIP" type="button" class="rounded px-2 py-1 text-sm font-semibold text-white shadow-sm bg-blue-500 hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500" :class="checkPeriod(row.key) ? 'opacity-70' : ''">Generuj EDI</button>
                            <span class="text-gray-200" v-else>Brak danych</span>
                        </td>
                    </tr>

                    <tr v-if="!periods.length">
                      <td colspan="4" class="py-6 text-center text-sm text-gray-500">Załaduj pierwszą paczkę okresów.</td>
                    </tr>
                    </tbody>
                </table>

                <!-- Strażnik do infinite loading -->
                <div ref="sentinel" class="h-6 w-full"></div>
            </div>
            <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button type="button" class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm" @click="close" ref="cancelButtonRef">Zamknij</button>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { ref, nextTick } from 'vue'
import {db} from "@/firebase/gfbconf.js";
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import moment from 'moment';
import randomstring from 'randomstring';

const DISCOVERY_BATCH = 300;
const COUNT_PAGE_SIZE = 1000;
const COUNT_CONCURRENCY = 3;
const FETCH_PAGE = 500;

export default {
  components: {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
  },
  props: ["modalData"],
  data() {
    return {
        periods: [],
        isLoading: false,
        canLoadMore: false,
        lastDoc: null,
        countQueue: [],
        inflight: new Set(),
        counted: new Set(),
        countEpoch: 0,
        io: null
    }
  },
  methods: {
    parsePeriod(str) {
      if (typeof str !== 'string') return null;
      const [a, b] = str.split(' - ');
      if (!a || !b) return null;
      const parseOne = (s) => {
        const [d,m,y] = s.split('-').map(n => parseInt(n,10));
        if (!d || !m || !y) return null;
        const pad = n => String(n).padStart(2,'0');
        return { d, m, y, iso: `${y}-${pad(m)}-${pad(d)}` };
      };
      const p1 = parseOne(a), p2 = parseOne(b);
      if (!p1 || !p2) return null;
      return {
        start: Date.UTC(p1.y, p1.m-1, p1.d, 0,0,0),
        end:   Date.UTC(p2.y, p2.m-1, p2.d, 23,59,59),
        startISO: p1.iso, endISO: p2.iso
      };
    },
    checkPeriod(period) {
        if(moment(period.slice(0, period.indexOf(' - ')), 'DD-MM-YYYY').unix() >= 1712527200 && moment(period.slice(period.indexOf(' - ')+3), 'DD-MM-YYYY').unix() >= 1712527200) {
            return false
        } else {
            return true
        }
    },
    log(log){
        console.log(log)
    },

    /* ------- Odkrywanie okresów -------- */
    async loadFirstPage() {
        this.periods = [];
        this.lastDoc = null;
        this.countQueue = [];
        this.inflight.clear();
        this.counted.clear();
        this.countEpoch++;
        this.detachObserver();
        await this.loadMore();
        this.attachObserver();
    },
    async loadMore(batch = DISCOVERY_BATCH) {
      if (this.isLoading) return;
      this.isLoading = true;
      try {
        let q = db.collection("Invoices").orderBy("created.unix","desc").limit(batch);
        if (this.lastDoc) q = q.startAfter(this.lastDoc);
        const snap = await q.get();
        const docs = snap.docs;
        if (docs.length) this.lastDoc = docs[docs.length-1];

        const byKey = new Map(this.periods.map(p => [p.key, p]));
        const newly = [];

        for (const d of docs) {
          const inv = d.data();
          const period = inv?.period;
          if (!period) continue;
          if (!byKey.has(period)) {
            const obj = {
              key: period,
              meta: { count: null, loading: true },
              firstUnix: inv?.created?.unix ?? 0,
              parsed: this.parsePeriod(period),
              hasPayerNIP: inv.payerNIP !== undefined
            };
            byKey.set(period, obj);
            newly.push(period);
          } else {
            const obj = byKey.get(period);
            const unix = inv?.created?.unix ?? 0;
            if (unix > obj.firstUnix) obj.firstUnix = unix;
            if (!obj.hasPayerNIP && inv.payerNIP !== undefined) {
              obj.hasPayerNIP = true;
            }
          }
        }

        const arr = Array.from(byKey.values());
        arr.sort((A,B)=>{
          const a=A.parsed,b=B.parsed;
          if (a&&b){ if (b.start!==a.start) return b.start-a.start; if (b.end!==a.end) return b.end-a.end; return B.key.localeCompare(A.key); }
          if (A.firstUnix!==B.firstUnix) return B.firstUnix-A.firstUnix;
          return B.key.localeCompare(A.key);
        });

        this.periods = arr;
        this.canLoadMore = docs.length === batch;

        this.enqueueCounts(newly);
        this.kickCounters();

        await nextTick();
        this.attachObserver();
      } catch (e) {
        console.error(e);
        alert('Nie udało się załadować okresów.');
      } finally {
        this.isLoading = false;
      }
    },

    /* ------- Kolejka liczników (doc length) ------- */
    enqueueCounts(keys){
        for (const k of keys){
            if (!this.counted.has(k)) {
            const row = this.periods.find(p => p.key === k);
            if (row) { row.meta.loading = true; row.meta.count = null; }
            this.countQueue.push(k);
            }
        }
    },
    async kickCounters(){
        const epoch = this.countEpoch;
        while (this.inflight.size < COUNT_CONCURRENCY && this.countQueue.length){
            const key = this.countQueue.shift();
            if (!key || this.inflight.has(key) || this.counted.has(key)) continue;
            this.inflight.add(key);

            this.countByPaging(key)
            .then(cnt => {
                if (epoch !== this.countEpoch) return;
                this.applyCount(key, cnt);
            })
            .catch(e => {
                if (epoch !== this.countEpoch) return;
                console.warn('countByPaging fail', key, e);
                this.applyCount(key, null);
            })
            .finally(()=>{
                this.inflight.delete(key);
                if (epoch !== this.countEpoch) return;
                if (this.countQueue.length) this.kickCounters();
            });
        }
    },
    async countByPaging(period, pageSize = COUNT_PAGE_SIZE){
      let total = 0;
      let base = db.collection("Invoices").where("period","==",period).orderBy("created.unix","desc").limit(pageSize);
      let snap = await base.get();
      total += snap.size;
      while (snap.size === pageSize){
        const last = snap.docs[snap.docs.length-1];
        snap = await db.collection("Invoices")
              .where("period","==",period)
              .orderBy("created.unix","desc")
              .startAfter(last)
              .limit(pageSize)
              .get();
        total += snap.size;
      }
      return total;
    },
    applyCount(key, count){
      const row = this.periods.find(p => p.key===key);
      if (!row) return;
      row.meta.loading = false;
      row.meta.count = (typeof count === 'number') ? count : null;
      this.counted.add(key);
    },

    /* ------- Infinite loading ------- */
    attachObserver(){
      if (!this.$refs.sentinel || !('IntersectionObserver' in window)) return;
      if (this.io) this.io.disconnect();
      this.io = new IntersectionObserver((entries)=>{
        for (const e of entries){
          if (e.isIntersecting && this.canLoadMore && !this.isLoading){
            this.loadMore();
          }
        }
      }, { root: null, rootMargin: '0px', threshold: 0.1 });
      this.io.observe(this.$refs.sentinel);
    },
    detachObserver(){ if (this.io){ this.io.disconnect(); this.io=null; } },

    /* ------- Paged fetch do generowania ------- */
    async *fetchInvoicesForPeriod(period, pageSize = FETCH_PAGE){
      let q = db.collection("Invoices").where("period","==",period).orderBy("created.unix","desc").limit(pageSize);
      let snap = await q.get(); yield snap;
      while (!snap.empty && snap.size===pageSize){
        const last = snap.docs[snap.docs.length-1];
        snap = await db.collection("Invoices").where("period","==",period).orderBy("created.unix","desc").startAfter(last).limit(pageSize).get();
        yield snap;
      }
    },

    // Data sprzedaży dla EDI: ostatni dzień z period ("DD-MM-YYYY - DD-MM-YYYY" lub "DD-MM-YYYY"),
    // fallback do invoiceDeliveryDate gdy period nieparsowalny. Identyczna logika jak
    // computeSaleDateISO w Invoices.vue (sendToKsef/sendCorrectionToKsef) — dzięki temu
    // DataSprzed w EDI zgadza się 1:1 z saleDate wysyłanym do KSeF.
    computeSaleDateForEdi(period, fallbackDeliveryDate) {
      const fallbackMoment = moment(fallbackDeliveryDate, 'DD.MM.YYYY');
      const fallbackFmt = fallbackMoment.isValid() ? fallbackMoment.format('YY.MM.DD') : '';
      if (!period || typeof period !== 'string' || period.trim() === '' || period.trim() === 'Brak') {
        return fallbackFmt;
      }
      const trimmed = period.trim();
      let dateStr = trimmed;
      if (trimmed.includes(' - ')) {
        const parts = trimmed.split(' - ');
        dateStr = parts[parts.length - 1].trim();
      }
      const match = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (!match) return fallbackFmt;
      // match: [_, DD, MM, YYYY] → YY.MM.DD
      return `${match[3].slice(2)}.${match[2]}.${match[1]}`;
    },
    /* ------- Generowanie EDI z obsługą KSEF ------- */
    async generateEDI(period) {
        // Pobierz faktury dla tego okresu stronicowo
        const invoicesData = [];
        for await (const snap of this.fetchInvoicesForPeriod(period, FETCH_PAGE)){
          snap.forEach(doc => {
            invoicesData.push(doc.data());
          });
        }

        if(!invoicesData.length) {
          alert('Brak faktur w wybranym okresie.');
          return;
        }

        let content = `
[Info]
Program=Lauron
Kodowanie=Windows 1250
Nazwa=WB TANK SP. Z O.O.                         UDORPIE, ul. Chełmińska 3                  77-100 BYTÓW                               BDO000386131
Nip=8421761290
Konto=PKO                                     92 1020 4708 0000 7202 0048 0806
Kod=77-100
Miasto=BYTÓW
Data=${moment().format('YY.MM.DD')}
Godz=${moment().format('HH:mm:ss')}

[Okres]
Poczatek=${moment((period.slice(0, period.indexOf(' - '))).replaceAll('-', '.'), 'DD.MM.YYYY').format('YY.MM.DD')}
Koniec=${moment((period.slice(period.indexOf(' - ')+3)).replaceAll('-', '.'), 'DD.MM.YYYY').format('YY.MM.DD')}
        
        `

        // Zbieraj dane KSEF do osobnej sekcji
        let ksefEntries = [];
        
        for(let i = 0; i < invoicesData.length; i++) {
            let invoice = invoicesData[i]
            let invoiceContent = `
[Dokument]
Rejestr=FakturySpr
TypDok=2
DataSprzed=${this.computeSaleDateForEdi(invoice.period, invoice.invoiceDeliveryDate)}
DataWyst=${moment(invoice.invoiceDeliveryDate, 'DD.MM.YYYY').format('YY.MM.DD')}
Termin=${invoice.paymentDayAmount}
NrFilii=0
StanDok=70
NazwiskoOdbiorcy=
NrWZ=
NrOdbiorcy=0
SymbolOdbiorcy=${invoice.payerNIP}
NazwaOdbiorcy=${invoice.payerName}
AdresOdbiorcy=${invoice.payerAddress == null || invoice.payerAddress.length === 0 ? 'brak' : invoice.payerAddress}
Gotowka=0.00
Karta=0.00
Bon=0.00
Czek=0.00
Godzina=${moment.unix(invoice.created.unix).format('HH:mm:ss')}
WalutaNr=0
WalutaNazwa=
WalutaKurs=1.0000
WalutaTabela=
WalutaData=${moment(invoice.invoiceDeliveryDate, 'DD.MM.YYYY').format('YY.MM.DD')}
RejKsiegowy=0
NrDok=${invoice.invoiceName}
IdentyfikatorDok=
NrKontrahenta=
NrPlatnosci=
SposobPlatnosci=przelew
NazwaKontrahenta=${invoice.payerName}
AdresKontrahenta=${invoice.payerAddress == null || invoice.payerAddress.length === 0 ? 'brak' : invoice.payerAddress}
SymbolKontrahenta=${invoice.payerNIP}
Nip=${invoice.payerNIP}
Netto=${invoice.netValueOfInvoice}
Vat=${invoice.vatValueOfInvoice}
Brutto=${invoice.valueOfInvoice}
Inicjaly=RP
Uwagi=
GTU=GTU_2; 
WartWCenachZakupuNetto=0.00
Vat0_Index=33
Vat0_Procent=23
Vat0_Stawka=23 %
Vat0_Netto=${invoice.netValueOfInvoice}
Vat0_Brutto=${invoice.valueOfInvoice}
Vat0_Vat=${invoice.vatValueOfInvoice}
Vat0_NettoZakup=0.00
Vat0_BruttoZakup=0.00
Vat0_VatZakup=0.00
Magazyny=9
[ZawartoscDokumentu]`
            content += invoiceContent
            for(let y = 0; y < invoice.transactions.length; y++) {
                let transaction = invoice.transactions[y]
                let positionContent =`
[Poz${y+1}]
Nazwa=${transaction.name}
Symbol=${transaction.productCode == null || transaction.productCode.length === 0 ? 'brak' : transaction.productCode}
Jm=${transaction.unit}
Ilosc=${transaction.quantity}
CenaNetto=${transaction.netUnitPrice}
CenaBrutto=${transaction.grossUnitPrice}
WartoscNetto=${transaction.netValue}
WartoscBrutto=${transaction.grossValue}
Vat=${transaction.vatRate}
WartoscVat=${transaction.vatValue}
CenaZakupuNetto=0.00
WartoscZakupuNetto=0.00
WartoscZakupuVat=0.00
Rabat=0.000
Grupa=0
NrCeny=1
PKWIU=
CenaWaluta=${transaction.netUnitPrice}
WartoscWaluta=${transaction.netValue}
CenaPrzedRabatemNetto=${transaction.netUnitPrice}
CenaPrzedRabatemBrutto=${transaction.netValue}
UwagiPoz=
GTU=${transaction.gtu == null || transaction.gtu.length === 0 ? '' : transaction.gtu}
SymbolAktualny=${transaction.productCode == null || transaction.productCode.length === 0 ? 'brak' : transaction.productCode}
SymbolDostawcy=
Nazwa=${transaction.name}
NazwaDlaKasy=
Blokady=0
Opis=
Marza=0.000
Marza2=0.000
Kategoria=
GrupaNazwa=
Indeks=${transaction.productCode == null || transaction.productCode.length === 0 ? 'brak' : transaction.productCode}
Mag=0
`
                content += positionContent
            }

            // Zbierz dane KSEF jeśli faktura ma numer KSeF
            if(invoice.ksef !== null && invoice.ksef !== undefined && invoice.ksef.ksefNumber !== null) {
              ksefEntries.push({
                invoiceName: invoice.invoiceName,
                ksefNumber: invoice.ksef.ksefNumber,
                ksefMode: invoice.ksef.ksefMode !== undefined && invoice.ksef.ksefMode !== null ? invoice.ksef.ksefMode : 0,
                ksefDate: invoice.ksef.acquisitionDate !== undefined && invoice.ksef.acquisitionDate !== null ? invoice.ksef.acquisitionDate : '',
                ksefHash: invoice.ksef.invoiceHash !== undefined && invoice.ksef.invoiceHash !== null ? invoice.ksef.invoiceHash : ''
              });
            }
        }

        // Dodaj sekcję DOKUMENTYKSEF jeśli istnieją faktury z numerem KSeF
        if(ksefEntries.length > 0) {
          content += `
[NAGLOWEK]
DOKUMENTYKSEF
[ZAWARTOSC]
`
          for(let k = 0; k < ksefEntries.length; k++) {
            const entry = ksefEntries[k];
            // Struktura wg dokumentacji EDI++ 1.12 (Tabela 43):
            // 1. Pełny numer dokumentu - Tekst(30)
            // 2. Nr KSeF - Tekst(35)
            // 3. Tryb wystawienia (0-Normalna, 1-Awaryjna, 2-OFFLINE, 3-ONLINE) - Bajt
            // 4. Data nadania Nr KSeF - Data (format yyyymmddhhnnss)
            // 5. Skrót dokumentu - Tekst(512)
            const ksefDate = entry.ksefDate ? moment(entry.ksefDate).format('YYYYMMDDHHmmss') : '00000000000000';
            content += `${entry.invoiceName},${entry.ksefNumber},${entry.ksefMode},${ksefDate},${entry.ksefHash}\n`;
          }
        }

        const key = randomstring.generate({length: 10, charset: 'alphabetic'});
        let filename = `Faktury_${period.replace(' - ', '-')}_${key}.edi`

        const blob = new Blob([content], { type: 'text/edi' });
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.setAttribute('href', url)
        a.setAttribute('download', filename);
        a.click()
    },

    /* ------- Generowanie CSV ------- */
    async generateCSV(period) {
        // Pobierz faktury dla tego okresu stronicowo
        const invoicesData = [];
        for await (const snap of this.fetchInvoicesForPeriod(period, FETCH_PAGE)){
          snap.forEach(doc => {
            invoicesData.push(doc.data());
          });
        }

        if(!invoicesData.length) {
          alert('Brak faktur w wybranym okresie.');
          return;
        }

        let invoiceHeader = {
            index: 'L.p.',
            date: 'Data',
            executionDate: 'Data_wykonania',
            invoiceNumber: 'Numer_faktury',
            nip: 'NIP',
            netValue: 'Wartosc_netto',
            vatValue: 'Wartosc_VAT',
            grossValue: 'Wartosc_brutto',
            paymentDayAmount: 'Termin_platnosci',
            paymentForm: 'Forma_platnosci',
        };

        const invoices = [
            invoiceHeader
        ];

        let invoice = {
            index: null,
            date: '',
            executionDate: '',
            invoiceNumber: '',
            nip: '',
            netValue: null,
            vatValue: null,
            grossValue: null,
            paymentDayAmount: null,
            paymentForm: '',
        };
        
        for(let i=0; i<invoicesData.length; i++) {
            if(invoicesData[i].vatValueOfInvoice === undefined && invoicesData[i].netValueOfInvoice === undefined){
                invoicesData[i].vatValueOfInvoice = 0
                invoicesData[i].netValueOfInvoice = 0
                for (let y = 0; y < invoicesData[i].transactions.length; y++) {
                    invoicesData[i].netValueOfInvoice = invoicesData[i].netValueOfInvoice + invoicesData[i].transactions[y].netValue
                    invoicesData[i].vatValueOfInvoice = invoicesData[i].vatValueOfInvoice + invoicesData[i].transactions[y].vatValue
                }
                invoicesData[i].vatValueOfInvoice = Math.round(invoicesData[i].vatValueOfInvoice * 100) / 100
                invoicesData[i].netValueOfInvoice = Math.round(invoicesData[i].netValueOfInvoice * 100) / 100
            }
            invoice = {
                index: i+1,
                date: `${invoicesData[i].invoiceDeliveryDate}`,
                executionDate: `${invoicesData[i].invoiceDeliveryDate}`,
                invoiceNumber: `${invoicesData[i].invoiceName}`,
                nip: `${invoicesData[i].payerNIP}`,
                netValue: invoicesData[i].netValueOfInvoice.toFixed(2),
                vatValue: invoicesData[i].vatValueOfInvoice.toFixed(2),
                grossValue: invoicesData[i].valueOfInvoice.toFixed(2),
                paymentDayAmount: invoicesData[i].paymentDayAmount,
                paymentForm: 'przelew',
            };
            invoices.push(invoice)
        }
        const csvdata = this.csvmaker(invoices);
        this.download(csvdata, period);
    },
    csvmaker(data)
    {
        let csvRows = [];
        for(let i=0;i<data.length; i++)
        {
            const values = Object.values(data[i]).join(';');
            csvRows.push(values)
        }
        return csvRows.join('\n')
    },
    download(data, period)
    {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let counter = 0;
        while (counter < 8)
        {
            result += characters.charAt(Math.floor(Math.random() * characters.length));
            counter += 1;
        }
        const blob = new Blob([data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.setAttribute('href', url)
        a.setAttribute('download', `Faktury_${period.replace(' - ', '-')}_${result}.csv`);
        a.click()
    },
    emitClose()
    {
        this.$emit("close");
    },
    close()
    {
        this.emitClose();
        this.detachObserver();
        this.open = false
    },
  },
  computed: {
  },
  mounted(){
    this.loadFirstPage().then(()=>this.attachObserver());
  },
  beforeUnmount(){ this.detachObserver(); },
  setup() {
    const open = ref(true)
    const sentinel = ref(null)
    return {
      open,
      sentinel,
    }
  }
}
</script>

<style scoped>


</style>